/*
 * This Source Code Form is subject to the terms of the Mozilla Public
 * License, v. 2.0. If a copy of the MPL was not distributed with this
 * file, you can obtain one at https://mozilla.org/MPL/2.0/.
 *
 * Copyright Oxide Computer Company
 */
import type { ReactNode } from 'react'
import { Link } from 'react-router'

import { Error12Icon, PrevArrow12Icon } from '@oxide/design-system/icons/react'

import { api, navToLogin, useApiMutation } from '~/api/client'
import { Button } from '~/ui/lib/Button'

const GradientBackground = () => (
  <div
    // negative z-index avoids covering MSW warning banner
    className="fixed top-0 right-0 bottom-0 left-0 -z-10"
    style={{
      background:
        'radial-gradient(200% 100% at 50% 100%, var(--surface-default) 0%, var(--surface-secondary) 100%)',
    }}
  />
)

type Props = { children: ReactNode; message?: string }

export function ErrorPage({ children }: Props) {
  return (
    <div className="flex w-full justify-center pt-(--preview-banner-height)">
      <GradientBackground />
      <div className="relative flex w-full justify-between">
        <Link
          to="/"
          className="text-mono-sm text-default hover:text-raise flex items-center p-6"
        >
          <PrevArrow12Icon title="Select" className="text-secondary mr-2" />
          Back to console
        </Link>
        <SignOutButton className="mt-4 mr-6" />
      </div>
      <div className="bg-raise! shadow-modal absolute top-1/2 left-1/2 flex w-96 -translate-x-1/2 -translate-y-1/2 flex-col items-center justify-center space-y-4 rounded-lg p-8">
        <div className="my-2 flex h-12 w-12 items-center justify-center">
          <div className="bg-destructive-inverse absolute h-10 w-10 rounded-full opacity-40 motion-safe:animate-[ping_2s_cubic-bezier(0,0,0.2,1)_infinite]" />
          <Error12Icon className="text-error light:text-error-secondary relative h-8 w-8" />
        </div>

        <div className="space-y-2 text-center">{children}</div>
      </div>
    </div>
  )
}

export function NotFound() {
  return (
    <ErrorPage>
      <h1 className="text-sans-2xl">Page not found</h1>
      <p className="text-secondary">
        The page you are looking for doesn&apos;t exist or you may not have access to it.
      </p>
    </ErrorPage>
  )
}

export function SignOutButton({ className }: { className?: string }) {
  const logout = useApiMutation(api.logout, {
    onSuccess: () => navToLogin({ includeCurrent: false }),
  })
  return (
    <Button
      onClick={() => logout.mutate({})}
      className={className}
      size="sm"
      variant="ghost"
    >
      Sign out
    </Button>
  )
}
